<template>
  <div class="radio-card-group">
    <label v-if="label" class="radio-card-group__label">{{ label }}</label>
    <div
      class="radio-card-group__options"
      :class="[
        `radio-card-group__options--${layout}`,
        columns ? `radio-card-group__options--cols-${columns}` : '',
      ]"
    >
      <label
        v-for="option in options"
        :key="option.value"
        class="radio-card"
        :class="{
          'radio-card--active': isSelected(option.value),
          'radio-card--disabled': option.disabled,
        }"
      >
        <input
          type="radio"
          :name="name"
          :value="option.value"
          :checked="isSelected(option.value)"
          :disabled="option.disabled"
          @change="handleChange(option.value)"
        />
        <span v-if="option.icon" class="radio-card__icon">
          <BaseIcon :name="option.icon" :size="iconSize" />
        </span>
        <span class="radio-card__content">
          <span class="radio-card__title">
            {{ option.title || option.label }}
          </span>
          <span v-if="option.description" class="radio-card__description">
            {{ option.description }}
          </span>
        </span>
        <span v-if="option.badge" class="radio-card__badge">
          {{ option.badge }}
        </span>
      </label>
    </div>
    <span v-if="error" class="radio-card-group__error">{{ error }}</span>
  </div>
</template>

<script>
  import { computed } from 'vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';

  export default {
    name: 'RadioCardGroup',

    components: { BaseIcon },

    props: {
      modelValue: {
        type: [String, Number, Boolean, null],
        default: null,
      },
      options: {
        type: Array,
        required: true,
        validator: (value) => value.every((option) => 'value' in option),
      },
      label: {
        type: String,
        default: '',
      },
      name: {
        type: String,
        default: () => `radio-card-group-${Math.random().toString(36).slice(2, 8)}`,
      },
      layout: {
        type: String,
        default: 'stack',
        validator: (v) => ['stack', 'grid'].includes(v),
      },
      columns: {
        type: Number,
        default: null,
        validator: (v) => v === null || (Number.isInteger(v) && v >= 1 && v <= 4),
      },
      iconSize: {
        type: [Number, String],
        default: 18,
      },
      error: {
        type: String,
        default: '',
      },
    },

    emits: ['update:modelValue', 'change'],

    setup(props, { emit }) {
      const isSelected = (value) => props.modelValue === value;

      const handleChange = (value) => {
        emit('update:modelValue', value);
        emit('change', value);
      };

      const _columns = computed(() => props.columns);

      return { isSelected, handleChange, _columns };
    },
  };
</script>

<style scoped lang="scss">
  .radio-card-group {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;

    &__label {
      font-size: 0.8125rem;
      font-weight: 600;
      color: var(--slate-700);
    }

    &__options {
      display: flex;
      gap: 0.75rem;

      &--stack {
        flex-direction: column;
      }

      &--grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));

        @include lg-down {
          grid-template-columns: 1fr;
        }
      }

      &--cols-2 {
        grid-template-columns: repeat(2, 1fr);

        @include lg-down {
          grid-template-columns: 1fr;
        }
      }

      &--cols-3 {
        grid-template-columns: repeat(3, 1fr);

        @include lg-down {
          grid-template-columns: 1fr;
        }
      }

      &--cols-4 {
        grid-template-columns: repeat(4, 1fr);

        @include lg-down {
          grid-template-columns: 1fr 1fr;
        }
      }
    }

    &__error {
      font-size: 0.75rem;
      color: var(--red-600);
      margin-top: 0.25rem;
    }
  }

  .radio-card {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    padding: 0.75rem 1rem;
    border: 2px solid var(--slate-200);
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s ease;
    background: white;
    position: relative;
    user-select: none;

    @include hover {
      border-color: var(--slate-300);
      background: var(--slate-50);
    }

    &--active {
      border-color: var(--brand-500);
      background: var(--brand-50);

      .radio-card__icon {
        color: var(--brand-600);
      }

      .radio-card__title {
        color: var(--brand-700);
      }
    }

    &--disabled {
      opacity: 0.5;
      cursor: not-allowed;

      @include hover {
        border-color: var(--slate-200);
        background: white;
      }
    }

    input[type='radio'] {
      position: absolute;
      opacity: 0;
      pointer-events: none;
      width: 0;
      height: 0;
    }

    &__icon {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      color: var(--slate-500);
      margin-top: 0.125rem;
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 0.125rem;
      flex: 1;
      min-width: 0;
    }

    &__title {
      font-weight: 600;
      font-size: 0.875rem;
      color: var(--slate-900);
    }

    &__description {
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__badge {
      flex-shrink: 0;
      align-self: center;
      font-size: 0.6875rem;
      font-weight: 600;
      color: var(--brand-700);
      background: var(--brand-100);
      padding: 0.125rem 0.5rem;
      border-radius: 0.625rem;
      text-transform: uppercase;
      letter-spacing: 0.03em;
    }
  }
</style>
